/*!
 * plp-restore.js — Back-button fix for "Show more" on collection pages
 * Theme: Be Yours 7.2.2 (<infinite-scroll> element, loads the next page via fetch
 * and appends it to #product-grid without touching the URL).
 *
 * What it does:
 *  1. Counts how many "Show more" pages the visitor loaded and stores it in
 *     history.state (no URL change -> no new crawlable URLs, no SEO side effects).
 *  2. Remembers which product card was clicked (sessionStorage).
 *  3. On Back, replays the theme's own "Show more" N-1 times, then scrolls to
 *     the product the visitor left from.
 *
 * Install: Assets > add plp-restore.js, then in layout/theme.liquid before </body>:
 * {% if template.name == 'collection' %}<script src="{{ 'plp-restore.js' | asset_url }}" defer></script>{% endif %}
 */
(() => {
  const grid = () => document.getElementById('product-grid');
  if (!grid()) return;
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

  const MAX_PAGES = 15;
  const KEY = 'plp:' + location.pathname + location.search;
  const items = () => (grid() ? grid().children.length : 0);
  const loadedPages = () => (history.state && history.state.plpLoaded) || 1;
  const setLoaded = (n) => {
    try {
      history.replaceState(Object.assign({}, history.state, { plpLoaded: n }), '');
    } catch (e) {}
  };
  // The theme swaps the <infinite-scroll> element after every load, so always re-query.
  const findBtn = () => {
    const b = document.querySelector('infinite-scroll');
    return b && !b.classList.contains('loading') && !b.classList.contains('disabled') ? b : null;
  };
  const waitFor = (fn, ms = 6000) =>
    new Promise((resolve) => {
      const t0 = Date.now();
      (function tick() {
        const v = fn();
        if (v) return resolve(v);
        if (Date.now() - t0 > ms) return resolve(null);
        setTimeout(tick, 80);
      })();
    });
  const read = () => {
    try {
      return JSON.parse(sessionStorage.getItem(KEY) || 'null');
    } catch (e) {
      return null;
    }
  };
  const write = (o) => {
    try {
      sessionStorage.setItem(KEY, JSON.stringify(o));
    } catch (e) {}
  };

  let replaying = false;
  let anchor = null;

  // 1) Track pages loaded (works for click and for scroll-triggered loading)
  let last = items();
  let timer = null;
  new MutationObserver(() => {
    if (replaying) return;
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (items() > last) setLoaded(Math.min(loadedPages() + 1, MAX_PAGES));
      last = items();
    }, 250);
  }).observe(grid(), { childList: true });

  // 2) Remember the clicked product card
  document.addEventListener(
    'click',
    (e) => {
      const a = e.target.closest && e.target.closest('a[href*="/products/"]');
      if (!a || !grid().contains(a)) return;
      const card = a.closest('#product-grid > *') || a;
      anchor = { path: new URL(a.href, location.href).pathname, top: card.getBoundingClientRect().top };
      write({ y: scrollY, a: anchor });
    },
    true
  );
  addEventListener('pagehide', () => write({ y: scrollY, a: anchor }));

  // 3) Restore on Back / reload
  async function restore() {
    const nav = performance.getEntriesByType('navigation')[0];
    if (!nav || (nav.type !== 'back_forward' && nav.type !== 'reload')) return;
    const target = Math.min(loadedPages(), MAX_PAGES);
    const saved = read();
    if (target <= 1 && !saved) return;

    const html = document.documentElement;
    const done = () => html.classList.remove('plp-restoring');
    if (target > 1) {
      const st = document.createElement('style');
      st.textContent = 'html.plp-restoring #MainContent{opacity:0}#MainContent{transition:opacity .15s}';
      document.head.appendChild(st);
      html.classList.add('plp-restoring');
      setTimeout(done, 9000); // failsafe
    }

    let reached = 1;
    replaying = true;
    for (let p = 1; p < target; p++) {
      const btn = await waitFor(findBtn);
      if (!btn) break;
      const before = items();
      btn.click();
      if (!(await waitFor(() => items() > before))) break;
      reached++;
    }
    replaying = false;
    last = items();
    if (reached !== loadedPages()) setLoaded(reached);

    const scrollBack = () => {
      if (!saved) return;
      let card = null;
      if (saved.a) {
        const link = [...grid().querySelectorAll('a[href*="/products/"]')].find(
          (a) => new URL(a.href, location.href).pathname === saved.a.path
        );
        card = link && (link.closest('#product-grid > *') || link);
      }
      if (card) scrollTo(0, card.getBoundingClientRect().top + scrollY - saved.a.top);
      else if (typeof saved.y === 'number') scrollTo(0, saved.y);
    };
    scrollBack();
    done();
    setTimeout(scrollBack, 400); // re-apply once lazy images have settled
  }

  restore();
})();